import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { GALLERY } from "../data/siteData";

const CATS = ["All", ...new Set(GALLERY.map((g) => g.category))];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const items = filter === "All" ? GALLERY : GALLERY.filter((g) => g.category === filter);

  return (
    <main className="bg-[#FBF9F5] pt-28 lg:pt-36">
      <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-8 lg:pb-28">
        <Reveal>
          <Eyebrow>Selected Works</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-black tracking-tight text-[#1A2E20] sm:text-5xl lg:text-6xl">
            Proof, not promises.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#4a544c] sm:text-lg">
            A few recent properties across Southern Ontario. Tap any piece for the full story.
          </p>
        </Reveal>

        <Reveal className="mt-10 flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors duration-200 ${
                filter === c
                  ? "border-[#1A2E20] bg-[#1A2E20] text-[#FBF9F5]"
                  : "border-[#234E33]/20 text-[#1A2E20]/70 hover:border-[#2E6941] hover:text-[#2E6941]"
              }`}
            >
              {c}
            </button>
          ))}
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="portfolio-gallery-grid">
          <AnimatePresence mode="popLayout">
            {items.map((g) => (
              <motion.button
                layout
                key={g.title}
                type="button"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35 }}
                onClick={() => setActive(g)}
                data-testid={`gallery-item-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group overflow-hidden border border-[#234E33]/12 bg-[#FAF7F2] text-left"
              >
                <div className="overflow-hidden">
                  <img
                    src={g.url}
                    alt={g.title}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <p className="font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C86D51]">{g.category}</p>
                  <p className="mt-1 font-display text-lg font-bold text-[#1A2E20]">{g.title}</p>
                  <p className="mt-0.5 text-xs text-[#4a544c]">{g.scope}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#0C0E0D]/90 p-5 backdrop-blur-sm"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-h-[85vh] w-full max-w-4xl overflow-hidden bg-[#0C0E0D]"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.url} alt={active.title} className="max-h-[65vh] w-full object-cover" />
              <div className="p-6">
                <p className="font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#72B043]">{active.category}</p>
                <p className="mt-1 font-display text-2xl font-bold text-[#FBF9F5]">{active.title}</p>
                <p className="mt-1 text-sm text-[#FBF9F5]/60">{active.scope}</p>
              </div>
              <button
                type="button"
                onClick={() => setActive(null)}
                data-testid="gallery-lightbox-close"
                aria-label="Close"
                className="absolute right-4 top-4 rounded-full bg-[#FBF9F5]/10 p-2 text-[#FBF9F5] transition-colors hover:bg-[#72B043] hover:text-[#0C0E0D]"
              >
                <X size={20} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
